import {
  Button,
  Inline,
  Table,
  type TableProps,
  useViewport,
} from '@omnipresentgroup/design-system';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  useGetManagerExpenses,
} from '@omnipresentgroup/domains';
import {
  ExpensesColumnHeaders,
  useExpensesFilters,
  useExpensesTableConfig,
} from '@omnipresentgroup/features';
import {
  CATEGORY_PARAM,
  DATE_FROM_PARAMETER,
  DATE_TO_PARAMETER,
  EMPLOYEE_ID_PARAM,
  STATUS_PARAM,
} from '@omnipresentgroup/shared';
import {
  type BaseExpense,
  type ExpenseCategoryEnum,
  type ExpenseStatusEnum,
  SortOrder,
} from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ApproveExpensesAction } from '../expenses-approve';
import { DownloadExpensesAction } from '../expenses-download';
import { UploadExpensesAction } from '../expenses-upload';

const EMPTY_ARRAY: BaseExpense[] = [];
const LOADING_ARRAY = Array.from({ length: 10 }, () => ({}) as BaseExpense);

const DEFAULT_SORT_COLUMN: keyof BaseExpense = 'dateSpent';

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

type Props = {
  columnVisibility: Partial<{
    [key in keyof typeof ExpensesColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ExpensesColumnHeaders)[];
  optionsOverride?: Omit<TableOptions<BaseExpense>, 'data' | 'columns'>;
  columnsOverride?: ColumnDef<BaseExpense, unknown>[];
  onRowClick?: (row: BaseExpense) => void;
  useRowSelection?: boolean;
  showHeader?: boolean;
  showFilters?: boolean;
  usePagination?: boolean;
  emptyStateProps?: TableProps['emptyStateProps'];
  useSorting?: boolean;
  tableActions?: TableProps['tableActions'];
  onRowSelectionChange?: (expenses: BaseExpense[]) => void;
  clientId: string;
  isEditingMethodUpload?: boolean;
};

export const ExpensesList = ({
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  columnsOverride,
  emptyStateProps,
  onRowClick,
  clientId,
  showHeader = false,
  showFilters = false,
  useRowSelection = false,
  onRowSelectionChange,
  isEditingMethodUpload = false,
}: Props) => {
  const { isPhone } = useViewport();

  const [searchParameters] = useSearchParams();

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [expensesSelection, setExpensesSelection] = useState<BaseExpense[]>([]);

  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ExpensesColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: Number(DEFAULT_PAGE_INDEX),
    pageSize: Number(DEFAULT_PAGE_SIZE),
  });

  const [tableSorting, setTableSorting] = useState<SortingState>([
    {
      id: DEFAULT_SORT_COLUMN,
      desc: true,
    },
  ]);

  const getSortFromTableSorting = (sorting: SortingState) =>
    sorting[0].id as keyof BaseExpense;

  const getOrderFromTableSorting = (sorting: SortingState): SortOrder =>
    sorting[0].desc ? SortOrder.DESC : SortOrder.ASC;

  const employeeIdParameter = new URLSearchParams(searchParameters).get(
    EMPLOYEE_ID_PARAM,
  ) as string;
  const statusParameter = new URLSearchParams(searchParameters).get(
    STATUS_PARAM,
  ) as string;
  const categoryParameter = new URLSearchParams(searchParameters).get(
    CATEGORY_PARAM,
  ) as string;
  const dateFromParameter = new URLSearchParams(searchParameters).get(
    DATE_FROM_PARAMETER,
  ) as string;
  const dateToParameter = new URLSearchParams(searchParameters).get(
    DATE_TO_PARAMETER,
  ) as string;

  const queryProps = useMemo(() => {
    return {
      pageSize,
      pageIndex,
      employeeId: employeeIdParameter,
      status: statusParameter as ExpenseStatusEnum,
      category: categoryParameter as ExpenseCategoryEnum,
      dateFrom: dateFromParameter,
      dateTo: dateToParameter,
    };
  }, [
    pageSize,
    pageIndex,
    employeeIdParameter,
    statusParameter,
    categoryParameter,
    dateFromParameter,
    dateToParameter,
  ]);

  const {
    data,
    isLoading: isManagerExpensesLoading,
    isError: isManagerExpensesError,
  } = useGetManagerExpenses({
    clientId,
    page: queryProps.pageIndex + 1,
    size: queryProps.pageSize,
    employeeId: queryProps.employeeId,
    status: queryProps.status,
    category: queryProps.category,
    dateFrom: queryProps.dateFrom,
    dateTo: queryProps.dateTo,
    sorting: {
      sort: getSortFromTableSorting(tableSorting),
      order: getOrderFromTableSorting(tableSorting),
    },
  });

  const { Filters } = useExpensesFilters({
    expenses: data?.items ?? [],
    clientId,
    enabled: showFilters,
  });

  const isLoading = useMemo(
    () => isManagerExpensesLoading,
    [isManagerExpensesLoading],
  );

  const isError = useMemo(
    () => isManagerExpensesError,
    [isManagerExpensesError],
  );

  const { columns } = useExpensesTableConfig({
    columnsOverride,
  });

  const paginationProps: TableProps['paginationProps'] = useMemo(() => {
    return {
      pageSize: queryProps.pageSize,
      pageIndex: queryProps.pageIndex,
      canNextPage: (data?.pages ?? 0) > queryProps.pageIndex + 1,
      canPreviousPage: queryProps.pageIndex > DEFAULT_PAGE_INDEX,
      pageCount: data?.total
        ? Math.ceil(Math.max(data.total / queryProps.pageSize, 1))
        : DEFAULT_PAGE_INDEX,
      totalCount: isLoading ? queryProps.pageSize : data?.total,
    };
  }, [
    queryProps.pageSize,
    queryProps.pageIndex,
    data?.pages,
    data?.total,
    isLoading,
  ]);

  const tableOptions: Omit<TableOptions<BaseExpense>, 'data' | 'columns'> = {
    state: {
      columnVisibility: columnVisibilityState,
      columnOrder,
      ...(rowSelection ? { rowSelection } : {}),
      sorting: tableSorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibilityState,
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: (updater) => {
      const newRowSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newRowSelection);

      const currentPageSelectedExpenses =
        data?.items.filter((row) =>
          Object.keys(newRowSelection).includes(row.id),
        ) ?? [];

      const updatedSelectedRows = [
        ...expensesSelection.filter(
          (row) => !data?.items.some((item) => item.id === row.id),
        ),
        ...currentPageSelectedExpenses,
      ];

      setExpensesSelection(updatedSelectedRows);
      onRowSelectionChange?.(updatedSelectedRows);
    },
    enableRowSelection: isLoading ? false : useRowSelection,
    onPaginationChange: setPagination,
    manualPagination: true,
    enableSortingRemoval: false,
    sortDescFirst: true,
    onSortingChange: setTableSorting,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    ...optionsOverride,
  };

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : (data?.items ?? EMPTY_ARRAY),
    columns: columns,
    getRowId: (row: BaseExpense) => row.id,
    ...tableOptions,
  });

  return (
    <Table
      id="expenses-list"
      table={table}
      loading={isLoading}
      title="Expenses"
      showHeader={showHeader || showFilters}
      showErrorState={isError}
      errorStateProps={{
        id: 'expenses-list-error',
        title: ERROR_STATE_TITLE,
        description: ERROR_STATE_DESCRIPTION,
        callToAction: <Button onClick={window.location.reload}>Retry</Button>,
      }}
      onRowClick={
        onRowClick
          ? (row: BaseExpense) => {
              !isLoading && !isError && onRowClick(row);
            }
          : undefined
      }
      emptyStateProps={emptyStateProps}
      useSorting
      usePagination
      paginationProps={paginationProps}
      useRowSelection={useRowSelection}
      filterSection={<Filters />}
      tableActions={
        <Inline gap="8">
          <DownloadExpensesAction
            companyId={clientId}
            expenses={expensesSelection}
            iconOnly={isPhone}
            onSuccess={() => {
              setRowSelection({});
              setExpensesSelection([]);
            }}
          />
          {isEditingMethodUpload ? (
            <UploadExpensesAction iconOnly={isPhone} />
          ) : (
            <ApproveExpensesAction
              expenses={expensesSelection}
              iconOnly={isPhone}
              onSuccess={() => {
                setRowSelection({});
                setExpensesSelection([]);
              }}
            />
          )}
        </Inline>
      }
    />
  );
};
